// src/components/CreateEvent.jsx
// ============================================================
// CREATE EVENT — the invitation to bring people together
//
// Not a form. A conversation.
// "What are you planning?" → "When?" → "Where?" → "Tell people why"
// ============================================================

import React, { useState, useRef } from 'react';
import {
  ArrowLeft,
  Calendar,
  CaretRight,
  Check,
  Clock,
  CurrencyDollar,
  Globe,
  Info,
  Lock,
  MapPin,
  Sparkle,
  Users,
  X
} from '@phosphor-icons/react';
import { API_BASE as API_URL } from '../apiConfig';


const CATEGORIES = [
  { id: 'Sports & Fitness', icon: '⚽', desc: 'Running, gym, yoga, team sports, hiking' },
  { id: 'Music & Concerts', icon: '🎵', desc: 'Live music, jam sessions, festivals' },
  { id: 'Food & Drink', icon: '🍕', desc: 'Coffee catch-ups, dinners, cooking, tastings' },
  { id: 'Arts & Creative', icon: '🎨', desc: 'Art, photography, writing, crafts' },
  { id: 'Outdoors & Nature', icon: '🌿', desc: 'Birdwatching, fishing, camping, walks' },
  { id: 'Social & Meetups', icon: '☕', desc: 'Coffee chats, book clubs, board games' },
  { id: 'Technology', icon: '💻', desc: 'Coding, gaming, tech talks' },
  { id: 'Learning', icon: '📚', desc: 'Workshops, talks, skill sharing' },
  { id: 'Wellness', icon: '🧘', desc: 'Meditation, mental health, support groups' },
  { id: 'Community', icon: '🤝', desc: 'Volunteering, cleanups, charity events' },
  { id: 'Family', icon: '👨‍👩‍👧', desc: 'Kids activities, family outings, parenting' },
  { id: 'Other', icon: '✨', desc: 'Anything that brings people together' },
];

const VIBES = [
  { id: 'casual', label: 'Casual & relaxed', desc: 'No pressure, just show up' },
  { id: 'active', label: 'Active & energetic', desc: 'Get moving, get outdoors' },
  { id: 'social', label: 'Social & chatty', desc: 'Conversation is the main event' },
  { id: 'focused', label: 'Focused & learning', desc: 'Come to learn or build something' },
  { id: 'chill', label: 'Quiet & reflective', desc: 'Low-key, peaceful, meditative' },
];

export default function CreateEvent({ token, onDone, onBack }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [vibe, setVibe] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [venueName, setVenueName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('Townsville');
  const [isOnline, setIsOnline] = useState(false);
  const [maxAttendees, setMaxAttendees] = useState('');
  const [isFree, setIsFree] = useState(true);
  const [price, setPrice] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [skillLevel, setSkillLevel] = useState('all');
  const [whatToBring, setWhatToBring] = useState('');
  const [isAllAges, setIsAllAges] = useState(false);
  const [authorityDeclared, setAuthorityDeclared] = useState(false);

  const ACCENT = '#2dd4bf';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const startDate = new Date(`${date}T${startTime}`);
      const endDate = endTime ? new Date(`${date}T${endTime}`) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

      const eventData = {
        title,
        description,
        shortDescription: description.substring(0, 200),
        category,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        location: isOnline ? {
          isOnline: true,
          venueName: 'Online',
        } : {
          venueName,
          address: { street: address, city, state: 'QLD', country: 'Australia' },
        },
        pricing: { isFree, amount: isFree ? 0 : parseFloat(price) || 0, currency: 'AUD' },
        capacity: { maxAttendees: maxAttendees ? parseInt(maxAttendees) : null },
        visibility: isPrivate ? 'private' : 'public',
        ageRestriction: { isAllAges },
        eventDetails: {
          skillLevel: skillLevel === 'all' ? '' : skillLevel,
          whatToBring,
        },
        tags: [vibe].filter(Boolean),
        authorityDeclaration: authorityDeclared
          ? 'By creating this event, I declare that I have the authority to do so.'
          : '',
      };

      const res = await fetch(`${API_URL}/api/connect/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(eventData),
      });

      const data = await res.json();
      if (data.success) {
        onDone && onDone(data.data);
      } else {
        setError(data.message || 'Something went wrong');
      }
    } catch (e) {
      setError('Could not create event. Please try again.');
    }
    setSubmitting(false);
  };

  const canAdvance = () => {
    if (step === 0) return !!category;
    if (step === 1) return title.length >= 3;
    if (step === 2) return !!date && !!startTime;
    if (step === 3) return isOnline || (venueName && city);
    if (step === 4) return true; // optional details
    return true;
  };

  const S = {
    page: { minHeight: '100dvh', background: '#09090b', color: '#f4f4f5', fontFamily: "'Inter', sans-serif" },
    header: { padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #1a1a1e' },
    backBtn: { background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: 4 },
    content: { padding: '24px 20px', maxWidth: 480, margin: '0 auto' },
    heading: { fontSize: 22, fontWeight: 700, margin: '0 0 6px', fontFamily: "var(--font-serif)" },
    sub: { fontSize: 14, color: '#a1a1aa', margin: '0 0 24px', lineHeight: 1.5 },
    catGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    catBtn: (selected) => ({
      background: selected ? 'rgba(45,212,191,0.1)' : '#111113',
      border: `1px solid ${selected ? 'rgba(45,212,191,0.4)' : '#27272a'}`,
      borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
      transition: 'all 0.15s',
    }),
    input: {
      width: '100%', padding: '14px 16px', background: '#111113', border: '1px solid #27272a',
      borderRadius: 12, color: '#f4f4f5', fontSize: 15, fontFamily: 'inherit', outline: 'none',
      transition: 'border-color 0.2s',
    },
    textarea: {
      width: '100%', padding: '14px 16px', background: '#111113', border: '1px solid #27272a',
      borderRadius: 12, color: '#f4f4f5', fontSize: 15, fontFamily: 'inherit', outline: 'none',
      resize: 'vertical', minHeight: 120, lineHeight: 1.6,
    },
    label: { fontSize: 13, color: '#a1a1aa', marginBottom: 6, display: 'block', fontWeight: 500 },
    row: { display: 'flex', gap: 12, marginBottom: 16 },
    nextBtn: (disabled) => ({
      width: '100%', padding: '16px', background: disabled ? '#27272a' : ACCENT,
      color: disabled ? '#52525b' : '#09090b', border: 'none', borderRadius: 14,
      fontSize: 15, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit', marginTop: 24, transition: 'all 0.2s',
    }),
    progress: { display: 'flex', gap: 4, marginBottom: 20 },
    dot: (active, done) => ({
      flex: 1, height: 3, borderRadius: 2,
      background: done ? ACCENT : active ? 'rgba(45,212,191,0.4)' : '#27272a',
      transition: 'all 0.3s',
    }),
    toggle: (on) => ({
      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
      background: on ? 'rgba(45,212,191,0.08)' : '#111113',
      border: `1px solid ${on ? 'rgba(45,212,191,0.3)' : '#27272a'}`,
      borderRadius: 12, cursor: 'pointer', marginBottom: 10,
    }),
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}><ArrowLeft size={20} /></button>
        <span style={{ fontSize: 15, fontWeight: 600 }}>Create something</span>
      </div>

      <div style={S.content}>
        {/* Progress */}
        <div style={S.progress}>
          {[0,1,2,3,4].map(i => <div key={i} style={S.dot(step === i, step > i)} />)}
        </div>

        {/* Step 0: Category */}
        {step === 0 && (
          <div>
            <h2 style={S.heading}>What are you planning?</h2>
            <p style={S.sub}>Pick the vibe. Don't overthink it — you can always change it later.</p>
            <div style={S.catGrid}>
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setCategory(cat.id)} style={S.catBtn(category === cat.id)}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>{cat.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: category === cat.id ? ACCENT : '#e4e4e7', marginBottom: 2 }}>{cat.id.split(' & ')[0]}</div>
                  <div style={{ fontSize: 11, color: '#71717a', lineHeight: 1.3 }}>{cat.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Title & Description */}
        {step === 1 && (
          <div>
            <h2 style={S.heading}>Tell people about it</h2>
            <p style={S.sub}>Keep it real. "Saturday morning birdwatching" works better than "Avian Observation Society Gathering #47".</p>

            <label style={S.label}>What's it called?</label>
            <input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Coffee and a chat, Sunrise yoga at The Strand..."
              style={S.input}
              maxLength={150}
            />

            <div style={{ height: 16 }} />

            <label style={S.label}>What should people expect?</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What will you do? Who's it for? Any experience needed? Just be yourself here — people respond to honesty, not marketing."
              style={S.textarea}
              maxLength={2000}
            />

            <div style={{ height: 16 }} />

            <label style={S.label}>What's the vibe?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {VIBES.map(v => (
                <button key={v.id} onClick={() => setVibe(v.id)} style={S.toggle(vibe === v.id)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: vibe === v.id ? ACCENT : '#e4e4e7' }}>{v.label}</div>
                    <div style={{ fontSize: 11, color: '#71717a' }}>{v.desc}</div>
                  </div>
                  {vibe === v.id && <Check size={16} color={ACCENT} />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: When */}
        {step === 2 && (
          <div>
            <h2 style={S.heading}>When's it happening?</h2>
            <p style={S.sub}>Give people enough notice to clear their schedule. Even "lazy Sunday afternoon" needs a date.</p>

            <label style={S.label}>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input}
              min={new Date().toISOString().split('T')[0]} />

            <div style={{ height: 16 }} />

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Start time</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>End time (optional)</label>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={S.input} />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Where */}
        {step === 3 && (
          <div>
            <h2 style={S.heading}>Where will you meet?</h2>
            <p style={S.sub}>A park bench, a coffee shop, a living room — anywhere people can gather.</p>

            <button onClick={() => setIsOnline(!isOnline)} style={S.toggle(isOnline)}>
              <Globe size={16} color={isOnline ? ACCENT : '#71717a'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isOnline ? ACCENT : '#e4e4e7' }}>This is online</div>
                <div style={{ fontSize: 11, color: '#71717a' }}>Video call, chat, or virtual meetup</div>
              </div>
              {isOnline && <Check size={16} color={ACCENT} />}
            </button>

            {!isOnline && (
              <>
                <div style={{ height: 8 }} />
                <label style={S.label}>Venue or meeting point</label>
                <input value={venueName} onChange={e => setVenueName(e.target.value)}
                  placeholder="e.g. Jezzine Barracks, The Strand, Riverway Lagoons..."
                  style={S.input} />

                <div style={{ height: 12 }} />
                <label style={S.label}>Address or area</label>
                <input value={address} onChange={e => setAddress(e.target.value)}
                  placeholder="Street address or general area"
                  style={S.input} />

                <div style={{ height: 12 }} />
                <label style={S.label}>City</label>
                <input value={city} onChange={e => setCity(e.target.value)} style={S.input} />
              </>
            )}
          </div>
        )}

        {/* Step 4: Details */}
        {step === 4 && (
          <div>
            <h2 style={S.heading}>A few more details</h2>
            <p style={S.sub}>All optional. Only fill in what matters for your event.</p>

            <button onClick={() => setIsPrivate(!isPrivate)} style={S.toggle(isPrivate)}>
              <Lock size={16} color={isPrivate ? ACCENT : '#71717a'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isPrivate ? ACCENT : '#e4e4e7' }}>Private event</div>
                <div style={{ fontSize: 11, color: '#71717a' }}>Only people you invite can see it</div>
              </div>
              {isPrivate && <Check size={16} color={ACCENT} />}
            </button>

            <button onClick={() => setIsFree(!isFree)} style={S.toggle(!isFree)}>
              <CurrencyDollar size={16} color={!isFree ? ACCENT : '#71717a'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: !isFree ? ACCENT : '#e4e4e7' }}>Has a cost</div>
                <div style={{ fontSize: 11, color: '#71717a' }}>Entry fee, materials, etc.</div>
              </div>
            </button>

            {!isFree && (
              <div style={{ marginBottom: 12 }}>
                <label style={S.label}>How much? (AUD)</label>
                <input type="number" value={price} onChange={e => setPrice(e.target.value)}
                  placeholder="0.00" style={{ ...S.input, width: 120 }} />
              </div>
            )}

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Max people (optional)</label>
                <input type="number" value={maxAttendees} onChange={e => setMaxAttendees(e.target.value)}
                  placeholder="No limit" style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Experience level</label>
                <select value={skillLevel} onChange={e => setSkillLevel(e.target.value)}
                  style={{ ...S.input, appearance: 'auto' }}>
                  <option value="all">All welcome</option>
                  <option value="beginner">Beginner friendly</option>
                  <option value="intermediate">Some experience</option>
                  <option value="advanced">Experienced</option>
                </select>
              </div>
            </div>

            <label style={S.label}>What to bring (optional)</label>
            <input value={whatToBring} onChange={e => setWhatToBring(e.target.value)}
              placeholder="e.g. Comfy shoes, a water bottle, your curiosity..."
              style={S.input} />

            <button onClick={() => setIsAllAges(!isAllAges)} style={{ ...S.toggle(isAllAges), marginTop: 12 }}>
              <Users size={16} color={isAllAges ? ACCENT : '#71717a'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isAllAges ? ACCENT : '#e4e4e7' }}>All ages welcome</div>
                <div style={{ fontSize: 11, color: '#71717a' }}>Kids, teens, everyone</div>
              </div>
              {isAllAges && <Check size={16} color={ACCENT} />}
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 12, padding: '12px 16px', marginTop: 16 }}>
            <p style={{ color: '#fca5a5', fontSize: 13, margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Navigation */}
        {step < 4 ? (
          <button onClick={() => canAdvance() && setStep(step + 1)} disabled={!canAdvance()} style={S.nextBtn(!canAdvance())}>
            {step === 0 ? "That's the one" : 'Next'}
          </button>
        ) : (
          <>
            {/* Authority declaration */}
            <div style={{
              padding: '12px 14px', marginBottom: 14,
              background: 'rgba(45,212,191,0.05)',
              border: `1px solid ${authorityDeclared ? 'rgba(45,212,191,0.3)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              transition: 'border-color 0.2s',
            }} onClick={() => setAuthorityDeclared(d => !d)}>
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                background: authorityDeclared ? '#2dd4bf' : 'transparent',
                border: `2px solid ${authorityDeclared ? '#2dd4bf' : 'rgba(255,255,255,0.2)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {authorityDeclared && <span style={{ fontSize: 11, color: '#0a0a0b', fontWeight: 700 }}>✓</span>}
              </div>
              <div>
                <p style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 600, margin: '0 0 2px' }}>
                  I have the authority to create this event
                </p>
                <p style={{ color: '#71717a', fontSize: 11, margin: 0, lineHeight: 1.5 }}>
                  By ticking this, you declare you are the organiser or have permission from the organiser.
                  If you don't tick this, the event will go to our moderation queue before going live.
                </p>
              </div>
            </div>
            <button onClick={handleSubmit} disabled={submitting} style={S.nextBtn(submitting)}>
              {submitting ? 'Creating...' : 'Bring people together'}
            </button>
          </>
        )}

        {step > 0 && (
          <button onClick={() => setStep(step - 1)}
            style={{ width: '100%', padding: 12, background: 'none', border: 'none', color: '#71717a', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8 }}>
            Go back
          </button>
        )}
      </div>
    </div>
  );
}
